import React from "react";
import { Col, FormGroup, Progress } from "reactstrap";
import { connect } from "react-redux";

import withStyles from "isomorphic-style-loader/withStyles";
import { withFirebase } from "react-redux-firebase";
import {
  isBlobArray,
  getImageMediaKey,
  getVideoMediaKey,
  isPreviewThumbnailReplacePayload,
  postHasCustomThumbnail,
  videoExtensionFromBlob,
} from "../../../utils/helpers";
import ReactTooltip from "react-tooltip";
import {
  ModalProps,
  serviceCaller,
  ServiceCallerProps,
  withModals,
} from "../../composition";
import firebase from "firebase/compat/app";
import "firebase/compat/auth";

import { IPost, PostType } from "../../../types/services/IPost";
import s from "./styles.scss";
import { isCurrentUserCollaborator } from "../../../utils/roleCheck";
import { IUploadProgress } from "../../../types/IUploadProgress";
import UseCharityLogoCheckbox from "../postDetail/useCharityLogoCheckbox";

interface Props extends ServiceCallerProps, ModalProps {
  currentPost?: {
    post: IPost;
    type: PostType;
  };
  postId: string;

  firebase: firebase.app.App;
  mediaPreview?: string;
  mediaBlobs?: Array<Blob | string>;
  busyLoading: "draft" | "publish" | "delete" | "export" | null;
  mediaUpdating?: boolean;

  updateVideoUrl: (url: string, isUploading: boolean) => void;
  updateImageUrl: (url: string, isUploading: boolean) => void;
  startUploading: (mediaKey: string, mediaPreview: string) => void;
  startPreviewThumbnailUpload: (mediaKey: string, mediaPreview: string) => void;
  updateCustomThumbnailUpload: (customThumbnailUpload: boolean) => void;
  updateProgress: (isUploading: boolean) => void;

  existingVideoMediaKey?: string | null;

  enableUseCharityLogoFeature: boolean;
  enableUseCharityLogo: boolean;
  updateEnableUseCharityLogo: (event) => void;

  thumbnailBlob?: Blob;
  customThumbnailUpload?: boolean;
  postTitle?: string;
}

interface State {
  postDataLoaded: boolean;
  mediaPreview?: string;

  mediaKey?: string;
  imageUpload?: IUploadProgress;
  videoUpload?: IUploadProgress;
  imageUrl?: string;
  videoUrl?: string;
  customThumbnailUpload: boolean;
  uploadError?: string | null;
}

class UploadMediaSection extends React.PureComponent<Props, State> {
  private retryUpload: (() => void) | null = null;

  constructor(props: Props) {
    super(props);
    this.state = {
      postDataLoaded: false,
      mediaPreview: this.props.mediaPreview,
      customThumbnailUpload: false,
    };
  }

  componentDidUpdate(prevProps: Props) {
    if (
      prevProps.mediaBlobs !== this.props.mediaBlobs &&
      this.props.mediaBlobs &&
      this.props.mediaBlobs.length > 0
    ) {
      const customThumbnailUpload = Boolean(this.props.customThumbnailUpload);
      const mediaBlobs = this.props.mediaBlobs;
      if (isPreviewThumbnailReplacePayload(mediaBlobs)) {
        this.setState(
          {
            videoUpload: null,
            imageUpload: null,
            customThumbnailUpload,
          },
          () => {
            this.props.updateCustomThumbnailUpload(customThumbnailUpload);
            this.startPreviewOnlyUpload(mediaBlobs[0], mediaBlobs[1]);
            this.props.clearMediaModalBlobs();
          }
        );
        return;
      }
      if (isBlobArray(this.props.mediaBlobs)) {
        this.setState(
          {
            videoUpload: null,
            imageUpload: null,
            customThumbnailUpload,
          },
          () => {
            this.props.updateCustomThumbnailUpload(customThumbnailUpload);
            if (isBlobArray(this.props.mediaBlobs)) {
              if (this.props.thumbnailBlob) {
                this.startMediaUpload([
                  this.props.thumbnailBlob,
                  this.props.mediaBlobs[1],
                ]);
              } else {
                this.startMediaUpload(this.props.mediaBlobs);
              }
              this.props.clearMediaModalBlobs();
            }
          }
        );
      }
    }
  }

  componentWillUnmount() {
    this.retryUpload = null;
    this.revokePreview(this.state.mediaPreview);
  }

  private revokePreview = (url?: string) => {
    if (url && url.startsWith("blob:")) {
      URL.revokeObjectURL(url);
    }
  };

  private setPreview = (blob: Blob): string => {
    const mediaPreview = URL.createObjectURL(blob);
    this.revokePreview(this.state.mediaPreview);
    return mediaPreview;
  };

  private failUpload = (message: string, error: unknown) => {
    console.error(message, error);
    this.setState({ uploadError: message }, () => this.props.updateProgress(true));
  };

  private settleIfComplete = () => {
    if (!this.isUploading()) {
      this.retryUpload = null;
    }
  };

  retry = () => {
    if (this.retryUpload) {
      this.retryUpload();
    }
  };

  getPostRef = () => {
    return this.props.firebase
      .database()
      .ref()
      .child(this.props.currentCharity.id)
      .child("pendingPosts")
      .child(this.props.postId);
  };

  mediaClicked = () => {
    if (!isCurrentUserCollaborator(this.props.currentCharity)) {
      return;
    }
    if (this.hasActiveTransfer() || (this.props.mediaUpdating && !this.state.uploadError)) {
      return;
    }
    if (this.props.currentCharity && !this.props.busyLoading) {
      const existingMedia: string[] = [];
      if (this.props.currentPost && this.props.currentPost.post.media) {
        const imageKey = getImageMediaKey(this.props.currentPost.post.media);
        const videoKey = getVideoMediaKey(this.props.currentPost.post.media);
        if (imageKey) {
          existingMedia.push(
            this.props.currentPost.post.media[imageKey].mediaUrl
          );
        }
        if (videoKey) {
          existingMedia.push(
            this.props.currentPost.post.media[videoKey].mediaUrl
          );
        }
      } else {
        if (this.state.imageUrl) {
          existingMedia.push(this.state.imageUrl);
        }
        if (this.state.videoUrl) {
          existingMedia.push(this.state.videoUrl);
        }
      }
      let existingPreviewUrl: string | undefined;
      let existingPreviewIsCustom = false;
      const post = this.props.currentPost?.post;
      const videoKey = post?.media ? getVideoMediaKey(post.media) : null;
      const preview =
        videoKey && post?.media ? post.media[`${videoKey}__preview`] : null;
      if (preview?.mediaUrl) {
        existingPreviewUrl = preview.mediaUrl;
        existingPreviewIsCustom = postHasCustomThumbnail(post);
      }
      this.props.toggleMediaModal(
        existingMedia,
        false,
        this.props.currentPost?.type === "published",
        false,
        existingPreviewUrl,
        existingPreviewIsCustom,
        this.props.postTitle
      );
    } else {
      console.error("Charity not loaded yet");
    }
  };

  startPreviewOnlyUpload = (thumbnailBlob: Blob, _videoUrl: string) => {
    const charityId = this.props.currentCharity.id;
    const postRef = this.getPostRef();
    const mediaKey = this.props.existingVideoMediaKey || this.state.mediaKey;
    if (!mediaKey) {
      console.error("Thumbnail replace requires a resolved video media key.");
      return;
    }
    this.retryUpload = () => this.startPreviewOnlyUpload(thumbnailBlob, _videoUrl);
    const mediaPreview = this.setPreview(thumbnailBlob);
    this.setState({
      mediaPreview,
      imageUrl: null,
      imageUpload: { totalBytes: thumbnailBlob.size, currentBytes: 0 },
      uploadError: null,
    });
    this.props.startPreviewThumbnailUpload(mediaKey, mediaPreview);
    const imagePath = `${charityId}/posts/${postRef.key}/${mediaKey}__preview.jpeg`;
    const imageRef = this.props.firebase.storage().ref(imagePath);
    const imageUpload = imageRef.put(thumbnailBlob);
    imageUpload.on(
      "state_changed",
      snapshot => {
        this.setState({
          imageUpload: {
            totalBytes: snapshot["totalBytes"],
            currentBytes: snapshot["bytesTransferred"],
          },
        });
      },
      error => this.failUpload("The thumbnail didn't upload.", error),
      () => {
        imageUpload.snapshot.ref
          .getDownloadURL()
          .then(url => {
            this.setState(
              {
                imageUrl: url,
              },
              () => {
                this.settleIfComplete();
                this.props.updateImageUrl(url, this.isUploading());
              }
            );
          })
          .catch(error => this.failUpload("The thumbnail didn't upload.", error));
      }
    );
  };

  startMediaUpload = (blobs: Blob[]) => {
    const charityId = this.props.currentCharity.id;
    const postRef = this.getPostRef();
    const mainImageRef = postRef.child("media").push();

    if (this.props.enableUseCharityLogo) {
      this.props.updateEnableUseCharityLogo({ target: { checked: false } });
    }

    this.retryUpload = () => this.startMediaUpload(blobs);
    const mediaPreview = this.setPreview(blobs[0]);
    // Mark every expected upload active before any Storage write, so save stays blocked until each URL settles.
    this.setState({
      mediaPreview,
      imageUrl: null,
      videoUrl: null,
      mediaKey: mainImageRef.key,
      imageUpload: { totalBytes: blobs[0].size, currentBytes: 0 },
      videoUpload: blobs[1] ? { totalBytes: blobs[1].size, currentBytes: 0 } : null,
      uploadError: null,
    });
    this.props.startUploading(mainImageRef.key, mediaPreview);

    //The first one will always be an image
    const imagePath = `${charityId}/posts/${postRef.key}/${mainImageRef.key}${
      blobs.length > 1 ? "__preview" : ""
    }.jpeg`;
    const imageRef = this.props.firebase.storage().ref(imagePath);
    const imageUpload = imageRef.put(blobs[0]);
    let videoUpload = null;
    if (blobs[1]) {
      const videoBlob = blobs[1];
      const videoExt = videoExtensionFromBlob(videoBlob);
      const videoPath = `${charityId}/posts/${postRef.key}/${mainImageRef.key}${videoExt}`;
      const videoRef = this.props.firebase.storage().ref(videoPath);
      videoUpload = videoRef.put(videoBlob, {
        contentType: videoBlob.type || "video/mp4",
      });
    }

    imageUpload.on(
      "state_changed",
      snapshot => {
        this.setState({
          imageUpload: {
            totalBytes: snapshot["totalBytes"],
            currentBytes: snapshot["bytesTransferred"],
          },
        });
      },
      error => this.failUpload("The image didn't upload.", error),
      () => {
        imageUpload.snapshot.ref
          .getDownloadURL()
          .then(url => {
            this.setState(
              {
                imageUrl: url,
              },
              () => {
                this.settleIfComplete();
                this.props.updateImageUrl(url, this.isUploading());
              }
            );
          })
          .catch(error => this.failUpload("The image didn't upload.", error));
      }
    );

    if (videoUpload) {
      videoUpload.on(
        "state_changed",
        snapshot => {
          this.setState(
            {
              videoUpload: {
                totalBytes: snapshot["totalBytes"],
                currentBytes: snapshot["bytesTransferred"],
              },
            },
            () => this.props.updateProgress(this.isUploading())
          );
        },
        error => this.failUpload("The video didn't upload.", error),
        () => {
          videoUpload.snapshot.ref
            .getDownloadURL()
            .then(url => {
              this.setState(
                {
                  videoUrl: url,
                },
                () => {
                  this.settleIfComplete();
                  this.props.updateVideoUrl(url, this.isUploading());
                }
              );
            })
            .catch(error => this.failUpload("The video didn't upload.", error));
        }
      );
    }
  };

  calculateUploadProgress = () => {
    if (!this.state.imageUpload) {
      return 0;
    } else {
      let totalBytes = this.state.imageUpload.totalBytes;
      if (this.state.videoUpload) {
        totalBytes += this.state.videoUpload.totalBytes;
      }

      let currentBytes = this.state.imageUpload.currentBytes;
      if (this.state.videoUpload) {
        currentBytes += this.state.videoUpload.currentBytes;
      }

      return 100 * (currentBytes / totalBytes);
    }
  };

  /** True while any expected upload has no URL yet, including a failed one; save stays blocked. */
  isUploading = () => {
    return (
      (this.state.imageUpload != null && !this.state.imageUrl) ||
      (this.state.videoUpload != null && !this.state.videoUrl)
    );
  };

  hasActiveTransfer = () => {
    return this.isUploading() && !this.state.uploadError;
  };

  postWithDefaultMedia = (): boolean => {
    let defaultMedia = !this.state.imageUrl;
    const { currentPost } = this.props;

    if (currentPost) {
      const postMedia = currentPost.post.media;
      const keyPostMedia = getImageMediaKey(postMedia);
      defaultMedia = keyPostMedia
        ? !Boolean(this.state.imageUrl) && postMedia[keyPostMedia].defaultMedia
        : true;
    }

    return defaultMedia;
  };

  hasVideoMedia = (): boolean => {
    if (this.state.videoUpload || this.state.videoUrl) {
      return true;
    }
    const { currentPost } = this.props;
    if (currentPost?.post.media) {
      return Boolean(getVideoMediaKey(currentPost.post.media));
    }
    return false;
  };

  render() {
    const { enableUseCharityLogo, currentCharity } = this.props;
    const backgroundImageUrl = enableUseCharityLogo
      ? `url('https://storage.googleapis.com/public-viewspark-production/CharityLogos/${currentCharity.id}.jpg')`
      : `url('${this.state.mediaPreview}')`;

    const hasVideo = this.hasVideoMedia();
    const showMediaUpdating =
      !this.state.uploadError && (this.isUploading() || Boolean(this.props.mediaUpdating));
    const showCustomThumbnailBadge =
      this.state.customThumbnailUpload ||
      Boolean(
        this.props.currentPost &&
          postHasCustomThumbnail(this.props.currentPost.post)
      );

    const mediaContainer = (
      <div
        id="media"
        className={s.previewWrap}
        onClick={this.mediaClicked}
      >
        <div
          style={{
            backgroundImage: backgroundImageUrl,
          }}
          className={s.preview}
        />
        {hasVideo && (
          <i
            className={`fa fa-play-circle ${s.previewPlay}`}
            aria-hidden="true"
          />
        )}
        {showCustomThumbnailBadge && (
          <>
            <span
              className={s.previewCustomThumb}
              data-tip
              data-for="postCustomThumbnailTip"
            >
              <i className="fa fa-camera" aria-hidden="true" />
            </span>
            <ReactTooltip
              id="postCustomThumbnailTip"
              type="info"
              place="top"
              effect="float"
            >
              Custom thumbnail
            </ReactTooltip>
          </>
        )}
        {this.state.imageUpload && (
          <Progress
            className={s.mediaProgress}
            value={this.calculateUploadProgress()}
            animated={this.hasActiveTransfer()}
            striped={this.hasActiveTransfer()}
            color={this.state.uploadError ? "danger" : this.isUploading() ? "default" : "success"}
          />
        )}
        {this.state.uploadError && (
          <div className={s.previewUploadError} role="alert">
            <span className={s.previewUpdatingLabel}>{this.state.uploadError}</span>
            <button
              type="button"
              className={s.previewRetryButton}
              onClick={event => {
                event.stopPropagation();
                this.retry();
              }}
            >
              Retry upload
            </button>
          </div>
        )}
        {showMediaUpdating && (
          <div
            className={s.previewUpdating}
            role="status"
            aria-busy="true"
            aria-label="Updating ViewSpark"
          >
            <i className={`fa fa-spinner fa-spin ${s.previewUpdatingIcon}`} aria-hidden />
            <span className={s.previewUpdatingLabel}>Updating ViewSpark</span>
          </div>
        )}
      </div>
    );

    return (
      <Col
        sm={{ size: 8, offset: 2 }}
        md={{ size: 8, offset: 2 }}
        lg={{ size: 8, offset: 2 }}
        xl={{ size: 3, offset: 2 }}
      >
        <FormGroup>
          <label className={s.titleLabel} htmlFor="media">
            * Add Video/Photo
          </label>
          {this.props.enableUseCharityLogoFeature && (
            <UseCharityLogoCheckbox
              enableUseCharityLogo={this.props.enableUseCharityLogo}
              updateEnableUseCharityLogo={this.props.updateEnableUseCharityLogo}
            />
          )}

          {mediaContainer}
        </FormGroup>
      </Col>
    );
  }
}

const mapStateToProps = state => {
  const { modals } = state;
  return {
    mediaBlobs: modals.mediaBlobs,
    thumbnailBlob: modals.thumbnailBlob,
    customThumbnailUpload: modals.customThumbnailUpload,
  };
};

export default withFirebase(
  serviceCaller(
    withModals(connect(mapStateToProps)(withStyles(s)(UploadMediaSection)))
  )
);
